import { PayloadAction } from '@reduxjs/toolkit';
import { call, put } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { toast } from 'react-toastify';
import axios from 'axios';
import { complianceAPI } from '@/api';
import type {
  BaseRequest,
  GetAlertLevelsSuccessResponse,
  ResetTransactionAlertsCategoriesRequest,
  TransactionAlert,
  TransactionAlerts,
  UpdateTransactionAlertRequest,
  UpdateTransactionAlertResponse,
} from '@/api';
import { complianceAlertLevelsActions } from '@/redux/actions';
import { complianceAlertsMappers } from '@/redux/mappers';
import i18n from '@/i18n';
import { COMMON_CONTAINER_ID } from '@/config';
import { parseError } from '@/redux/helpers';

function* listAlertLevels(action: PayloadAction<BaseRequest>): SagaIterator {
  try {
    const { payload } = action;

    const response: GetAlertLevelsSuccessResponse = yield call(
      complianceAPI.listAlertLevels,
      payload,
    );

    const data = complianceAlertsMappers.fetchAlertSettings(response);
    yield put(complianceAlertLevelsActions.setData(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(complianceAlertLevelsActions.setLoading(false));
  }
}

function* updateSettings(
  action: PayloadAction<UpdateTransactionAlertRequest>,
): SagaIterator {
  try {
    const { payload } = action;

    const response: UpdateTransactionAlertResponse = yield call(
      complianceAPI.updateAlertLevels,
      payload,
    );

    const data: TransactionAlerts =
      complianceAlertsMappers.fetchAlertLevels(response);

    yield put(complianceAlertLevelsActions.setData(data));

    toast.success(i18n.t('translation.compliance_alerts_updated'), {
      containerId: COMMON_CONTAINER_ID,
    });
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error?.response?.data?.reason) {
        const message = error?.response?.data?.reason;
        toast.error(message, {
          containerId: COMMON_CONTAINER_ID,
        });
      }
    }
  } finally {
    yield put(complianceAlertLevelsActions.setLoading(false));
  }
}

function* resetTransactionAlertsCategories(
  action: PayloadAction<ResetTransactionAlertsCategoriesRequest>,
): SagaIterator {
  try {
    const { payload } = action;

    yield call(complianceAPI.resetTransactionAlertsCategories, payload);

    toast.success(i18n.t('translation.compliance_settings_default_success'), {
      containerId: COMMON_CONTAINER_ID,
    });
  } catch (error: unknown) {
    const { message } = parseError(error);
    toast.error(message, { containerId: COMMON_CONTAINER_ID });
  } finally {
    yield put(complianceAlertLevelsActions.setLoading(false));
  }
}

export { listAlertLevels, updateSettings, resetTransactionAlertsCategories };
